import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { incidentApi } from '../../services/incidentApi';
import { useAuth } from '../../context/AuthContext';
import AssetSelector from '../../components/AssetSelector';

const PRIORITY_MAP = {
  critica: { cls: 'badge-critical', label: 'Critica', color: 'var(--red)', bg: 'var(--red-dim)' },
  alta:    { cls: 'badge-warning',  label: 'Alta',    color: '#D45D00', bg: 'var(--amber-dim)' },
  media:   { cls: 'badge-offline',  label: 'Media',   color: 'var(--ink-2)', bg: 'var(--bg-subtle)' },
  baja:    { cls: 'badge-active',   label: 'Baja',    color: 'var(--green)', bg: 'var(--green-dim)' },
};
const STATUS_MAP = {
  nuevo:       { color: 'var(--accent)', bg: '#E6F1FB', label: 'Nuevo' },
  en_progreso: { color: '#D45D00', bg: 'var(--amber-dim)', label: 'En progreso' },
  pendiente:   { color: 'var(--ink-2)', bg: 'var(--bg-subtle)', label: 'Pendiente' },
  resuelto:    { color: 'var(--green)', bg: 'var(--green-dim)', label: 'Resuelto' },
  cerrado:     { color: 'var(--ink-2)', bg: 'var(--bg-subtle)', label: 'Cerrado' },
};
const TABS = [
  { id: 'todos',         label: 'Todos' },
  { id: 'asignados',     label: 'Asignados a mi' },
  { id: 'sin_asignar',   label: 'Sin asignar' },
  { id: 'esperando',     label: 'Esperando usuario' },
  { id: 'resueltos_hoy', label: 'Resueltos hoy' },
];

function SLABar({ inc }) {
  if (['resuelto','cerrado'].includes(inc.status)) return <span style={{fontSize:11,color:'var(--green)'}}>&#10003; Resuelto</span>;
  const total = new Date(inc.sla_resolve_at) - new Date(inc.created_at);
  const elapsed = Date.now() - new Date(inc.created_at);
  const pct = Math.min(100, Math.round(elapsed / total * 100));
  const color = pct >= 100 ? 'var(--red)' : pct >= 80 ? '#D45D00' : 'var(--green)';
  const remaining = Math.max(0, Math.ceil((new Date(inc.sla_resolve_at) - Date.now()) / 3600000));
  return (
    <div style={{minWidth:100}}>
      <div style={{height:4,background:'#eee',borderRadius:4,overflow:'hidden',marginBottom:3}}>
        <div style={{height:'100%',width:`${pct}%`,background:color,borderRadius:4}} />
      </div>
      <span style={{fontSize:10,color}}>{pct >= 100 ? 'Vencido' : `${remaining}h restantes`}</span>
    </div>
  );
}

const MAIN_CATS = [
  { id: 'soporte',   label: 'Soporte Tecnico',         icon: String.fromCodePoint(0x2692),
    subcats: [ {id:'hardware',label:'Hardware'}, {id:'software',label:'Software'}, {id:'red',label:'Red / Conectividad'}, {id:'acceso',label:'Acceso / Permisos'}, {id:'impresion',label:'Impresion'}, {id:'telefonia',label:'Telefonia'}, {id:'vpn',label:'VPN'} ] },
  { id: 'servicio',  label: 'Pedido de Servicio',       icon: String.fromCodePoint(0x1F4DA),
    subcats: [ {id:'instalacion_sw',label:'Instalacion de software'}, {id:'cambio_equipo',label:'Cambio de equipo'}, {id:'hardware_req',label:'Requerimiento de hardware'}, {id:'compras',label:'Pedido de compras'} ] },
  { id: 'rrhh',      label: 'Recursos Humanos',         icon: String.fromCodePoint(0x1F464),
    subcats: [ {id:'alta_usuario',label:'Alta de usuario'}, {id:'baja_usuario',label:'Baja de usuario'}, {id:'cambio_sector',label:'Cambio de sector'} ] },
  { id: 'seguridad', label: 'Incidente de Seguridad',   icon: String.fromCodePoint(0x26A0),
    subcats: [ {id:'ransomware',label:'Posible Ransomware / archivos cifrados'}, {id:'malware',label:'Virus / Malware / comportamiento sospechoso'}, {id:'acceso_no_autorizado',label:'Acceso no autorizado'}, {id:'robo_perdida',label:'Robo o perdida de dispositivo'}, {id:'fuga_datos',label:'Posible fuga de informacion'}, {id:'phishing',label:'Email sospechoso / Phishing'}, {id:'otro_seguridad',label:'Otro incidente de seguridad'} ] },
];

const API_URL = window.location.origin + '/api';

export default function Incidents() {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('todos');
  const [search, setSearch] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [sortBy, setSortBy] = useState('priority');
  const [sortDir, setSortDir] = useState('asc');
  const [showNew, setShowNew] = useState(false);
  const [newNotifs, setNewNotifs] = useState(0);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [form, setForm] = useState({ title:'', description:'', category:'otro', subcategory:'', priority:'media', reported_by_name: user?.full_name||'', reported_by_email: user?.email||'' });
  const [modalStep, setModalStep] = useState(1);
  const [selectedMainCat, setSelectedMainCat] = useState('');
  const [saving, setSaving] = useState(false);
  const [kbSuggestions, setKbSuggestions] = useState([]);
  const [newPendingFiles, setNewPendingFiles] = useState([]);
  const esRef = useRef(null);
  const kbTimerRef = useRef(null);
  const newFileRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (filterPriority) params.priority = filterPriority;
    if (activeTab === 'asignados') params.assigned_to = user?.id;
    else if (activeTab !== 'todos') params.tab = activeTab;
    incidentApi.getAll(params)
      .then(inc => { setIncidents(Array.isArray(inc) ? inc : []); })
      .catch(() => setIncidents([]));
    incidentApi.getStats()
      .then(st => setStats(st))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, filterPriority, activeTab, user?.id]);

  useEffect(() => { load(); }, [load]);

  const PRIORITY_ORDER = { critica: 0, alta: 1, media: 2, baja: 3 };
  const STATUS_ORDER = { nuevo: 0, en_progreso: 1, pendiente: 2, resuelto: 3, cerrado: 4 };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const sortedIncidents = [...incidents].sort((a, b) => {
    let va, vb;
    if (sortBy === 'priority') { va = PRIORITY_ORDER[a.priority] ?? 9; vb = PRIORITY_ORDER[b.priority] ?? 9; }
    else if (sortBy === 'status') { va = STATUS_ORDER[a.status] ?? 9; vb = STATUS_ORDER[b.status] ?? 9; }
    else if (sortBy === 'sla') { va = a.sla_resolve_at ? new Date(a.sla_resolve_at).getTime() : Infinity; vb = b.sla_resolve_at ? new Date(b.sla_resolve_at).getTime() : Infinity; }
    else if (sortBy === 'created') { va = new Date(a.created_at).getTime(); vb = new Date(b.created_at).getTime(); }
    else return 0;
    return sortDir === 'asc' ? va - vb : vb - va;
  });

  useEffect(() => {
    const es = incidentApi.subscribeEvents((event) => {
      if (event.type === 'new_incident') { setNewNotifs(n => n + 1); load(); }
      else if (event.type === 'update_incident') { load(); }
    });
    esRef.current = es;
    return () => es.close();
  }, [load]);

  const searchKB = useCallback((title) => {
    clearTimeout(kbTimerRef.current);
    if (!title || title.trim().length < 3) { setKbSuggestions([]); return; }
    kbTimerRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/kb?q=${encodeURIComponent(title.trim())}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setKbSuggestions(Array.isArray(data) ? data.slice(0, 5) : []);
      } catch { setKbSuggestions([]); }
    }, 350);
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const newInc = await incidentApi.create({ ...form, asset_id: selectedAsset?.id || null });
      // Subir archivos adjuntos si hay
      if (newPendingFiles.length > 0 && newInc?.id) {
        const fd = new FormData();
        newPendingFiles.forEach(f => fd.append('files', f));
        const uploadRes = await fetch(`${API_URL}/incidents/${newInc.id}/attachments`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
        });
        if (!uploadRes.ok) {
          const errData = await uploadRes.json().catch(() => ({}));
          console.warn('Error subiendo adjuntos:', errData.error || uploadRes.status);
        }
      }
      setShowNew(false);
      setSelectedAsset(null);
      setKbSuggestions([]);
      setNewPendingFiles([]);
      setForm({ title:'', description:'', category:'otro', subcategory:'', priority:'media', reported_by_name: user?.full_name||'', reported_by_email: user?.email||'' }); setModalStep(1); setSelectedMainCat('');
      load();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const timeSince = (d) => {
    if (!d) return '—';
    const diff = Date.now() - new Date(d);
    const m = Math.floor(diff / 60000);
    if (m < 60) return `hace ${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `hace ${h}h`;
    return `hace ${Math.floor(h/24)}d`;
  };

  const isUsuario = user?.role === 'usuario';

  return (
    <div style={{maxWidth:1200}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <div>
          <div style={{fontSize:22,fontWeight:700,color:'var(--gray-900)'}}>{isUsuario ? 'Mis tickets' : 'Incidentes'}</div>
          <div style={{fontSize:13,color:'var(--gray-500)'}}>{isUsuario ? 'Seguimiento de mis solicitudes' : 'Gestion de tickets de soporte'}</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {newNotifs > 0 && (
            <div onClick={() => { setNewNotifs(0); load(); }} style={{background:'var(--red-dim)',color:'var(--red)',border:'1px solid #f5c0bc',borderRadius:8,padding:'6px 12px',fontSize:13,fontWeight:500,cursor:'pointer'}}>
              &#128276; {newNotifs} nuevo{newNotifs>1?'s':''} — click para actualizar
            </div>
          )}
          <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ Nuevo incidente</button>
        </div>
      </div>

      {stats && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:10,marginBottom:20}}>
          {[['Total',stats.total,'var(--blue)'],['Nuevos',stats.nuevos,'var(--blue)'],['En progreso',stats.en_progreso,'#D45D00'],['Resueltos',stats.resueltos,'var(--green)'],['Criticos',stats.criticos,'var(--red)'],['SLA vencido',stats.sla_vencido,'var(--red)']].map(([label,val,color]) => (
            <div key={label} className="card" style={{padding:'12px 14px'}}>
              <div style={{fontSize:11,color:'var(--gray-500)',marginBottom:4}}>{label}</div>
              <div style={{fontSize:22,fontWeight:700,color}}>{val||0}</div>
            </div>
          ))}
        </div>
      )}

      {!isUsuario && (
        <div style={{display:'flex',gap:2,marginBottom:16,borderBottom:'2px solid var(--gray-100)'}}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{padding:'8px 16px',border:'none',background:'none',cursor:'pointer',fontSize:13,fontWeight:500,color:activeTab===tab.id?'var(--blue)':'var(--gray-500)',borderBottom:activeTab===tab.id?'2px solid var(--blue)':'2px solid transparent',marginBottom:-2,transition:'all 0.12s'}}>
              {tab.label}
              {tab.id==='sin_asignar'&&stats?.nuevos>0&&<span style={{marginLeft:6,background:'#E6F1FB',color:'var(--blue)',borderRadius:10,padding:'1px 6px',fontSize:10}}>{stats.nuevos}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="card" style={{marginBottom:0}}>
        <div style={{display:'flex',gap:10,padding:'12px 16px',borderBottom:'1px solid var(--gray-100)',flexWrap:'wrap'}}>
          <input className="form-input" style={{flex:1,minWidth:200}} placeholder="Buscar..." value={search} onChange={e=>setSearch(e.target.value)} />
          <select className="form-input" style={{width:150}} value={filterPriority} onChange={e=>setFilterPriority(e.target.value)}>
            <option value="">Todas las prioridades</option>
            {Object.entries(PRIORITY_MAP).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
          <button className="btn btn-secondary" onClick={load} style={{padding:'8px 12px'}}>&#8635;</button>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{width:60}}>#</th>
                <th>Asunto</th>
                <th style={{width:120}}>Categoria</th>
                {['priority','status','sla','created'].map(col => {
                  const labels = { priority:'Prioridad', status:'Estado', sla:'SLA', created:'Creado' };
                  const widths = { priority:90, status:110, sla:130, created:80 };
                  const active = sortBy === col;
                  return (
                    <th key={col} style={{width:widths[col],cursor:'pointer',userSelect:'none',color:active?'var(--blue)':'inherit'}}
                      onClick={() => handleSort(col)}>
                      {labels[col]} {active ? (sortDir === 'asc' ? '↑' : '↓') : <span style={{color:'var(--gray-300)'}}>↕</span>}
                    </th>
                  );
                })}
                {!isUsuario && <th style={{width:130}}>Reportado por</th>}
                {!isUsuario && <th style={{width:130}}>Asignado a</th>}
                <th style={{width:60}}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{textAlign:'center',padding:40}}><div className="spinner" style={{margin:'0 auto'}} /></td></tr>
              ) : sortedIncidents.length === 0 ? (
                <tr><td colSpan={10} style={{textAlign:'center',color:'var(--gray-500)',padding:40}}>Sin incidentes en esta vista.</td></tr>
              ) : sortedIncidents.map(inc => {
                const pri = PRIORITY_MAP[inc.priority] || PRIORITY_MAP.media;
                const st = STATUS_MAP[inc.status] || STATUS_MAP.nuevo;
                return (
                  <tr key={inc.id} style={{cursor:'pointer'}} onClick={() => window.location.href=`/incidents/${inc.id}`}>
                    <td><span className="mono" style={{fontSize:11,color:'var(--gray-500)'}}>#{inc.ticket_number}</span></td>
                    <td>
                      <div style={{fontWeight:500,fontSize:13}}>{inc.title}</div>
                      {inc.asset_hostname&&<div style={{fontSize:11,color:'var(--gray-500)',marginTop:2,fontFamily:'monospace'}}>{inc.asset_hostname}</div>}
                    </td>
                    <td style={{fontSize:11,color:'var(--gray-500)',textTransform:'capitalize'}}>{inc.category}</td>
                    <td><span style={{color:pri.color,fontSize:11,fontWeight:500}}>{pri.label}</span></td>
                    <td><span style={{color:st.color,fontSize:11,fontWeight:500}}>{st.label}</span></td>
                    <td><SLABar inc={inc} /></td>
                    <td style={{fontSize:11,color:'var(--gray-500)'}}>{timeSince(inc.created_at)}</td>
                    {!isUsuario && <td style={{fontSize:12}}>{inc.reported_by_name}</td>}
                    {!isUsuario && <td style={{fontSize:12,color:inc.assigned_to_name?'var(--gray-900)':'var(--gray-400)',fontStyle:inc.assigned_to_name?'normal':'italic'}}>{inc.assigned_to_name||'Sin asignar'}</td>}
                    <td onClick={e=>e.stopPropagation()}><Link to={`/incidents/${inc.id}`} className="btn btn-secondary btn-sm">Ver</Link></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.45)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div style={{display:'flex',gap:16,width:'100%',maxWidth: modalStep===3 ? 960 : 600,maxHeight:'90vh',alignItems:'flex-start'}}>
          <div className="card" style={{width:'100%',maxWidth:600,padding:28,maxHeight:'90vh',overflowY:'auto',flex:1}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
              <div>
                <h2 style={{fontSize:17,fontWeight:600}}>Nuevo incidente</h2>
                <div style={{fontSize:12,color:'var(--gray-500)'}}>Paso {modalStep} de 3</div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={()=>{setShowNew(false);setSelectedAsset(null);}}>&#10005;</button>
            </div>

            {modalStep === 1 && (
              <div>
                <div style={{fontSize:13,color:'var(--gray-500)',marginBottom:16}}>Selecciona el tipo de incidente:</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  {MAIN_CATS.map(cat => (
                    <button key={cat.id} type="button"
                      onClick={() => { setSelectedMainCat(cat.id); setModalStep(2); }}
                      style={{padding:'20px 16px',border:cat.id==='seguridad'?'2px solid #F5C0BC':'2px solid var(--border)',borderRadius:10,background:cat.id==='seguridad'?'var(--red-dim)':'#fff',cursor:'pointer',textAlign:'center'}}>
                      <div style={{fontSize:28,marginBottom:8}}>{cat.icon}</div>
                      <div style={{fontSize:13,fontWeight:600,color:cat.id==='seguridad'?'var(--red)':'#444'}}>{cat.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {modalStep === 2 && (function(){
              const cat = MAIN_CATS.find(c => c.id === selectedMainCat);
              return (
                <div>
                  {selectedMainCat === 'seguridad' && (
                    <div style={{background:'var(--red-dim)',border:'1px solid #F5C0BC',borderRadius:8,padding:'12px 16px',marginBottom:16,fontSize:13,color:'var(--red)'}}>
                      <strong>Incidente de seguridad.</strong> Se creara con prioridad <strong>Critica</strong> y SLA de 15 minutos.
                    </div>
                  )}
                  <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>{cat && cat.label}</div>
                  <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:16}}>
                    {cat && cat.subcats.map(sub => (
                      <button key={sub.id} type="button"
                        onClick={() => { setForm(f => ({...f, category: selectedMainCat, subcategory: sub.id, priority: selectedMainCat==='seguridad'?'critica':f.priority})); setModalStep(3); }}
                        style={{padding:'12px 16px',border:'1px solid var(--border)',borderRadius:8,background:'var(--surface)',cursor:'pointer',textAlign:'left',fontSize:14}}>
                        {sub.label}
                      </button>
                    ))}
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={() => setModalStep(1)}>Volver</button>
                </div>
              );
            })()}

            {modalStep === 3 && (
              <form onSubmit={handleCreate}>
                {form.category === 'seguridad' && (
                  <div style={{background:'var(--red-dim)',border:'1px solid #F5C0BC',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'var(--red)'}}>
                    <strong>Incidente de seguridad - Prioridad Critica</strong>
                  </div>
                )}
                <div style={{fontSize:12,color:'var(--gray-500)',marginBottom:12,padding:'6px 10px',background:'var(--gray-50)',borderRadius:6}}>
                  {MAIN_CATS.find(c=>c.id===selectedMainCat) && MAIN_CATS.find(c=>c.id===selectedMainCat).label} &rsaquo; {MAIN_CATS.find(c=>c.id===selectedMainCat) && MAIN_CATS.find(c=>c.id===selectedMainCat).subcats.find(s=>s.id===form.subcategory) && MAIN_CATS.find(c=>c.id===selectedMainCat).subcats.find(s=>s.id===form.subcategory).label}
                </div>
                <div className="form-group"><label className="form-label">Titulo *</label><input className="form-input" value={form.title} onChange={e=>{setForm(f=>({...f,title:e.target.value}));searchKB(e.target.value);}} required /></div>
                <div className="form-group"><label className="form-label">Descripcion</label><textarea className="form-input" rows={3} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} style={{resize:'vertical',fontFamily:'inherit'}} /></div>
                {/* Adjuntos en creación */}
                <div className="form-group">
                  <label className="form-label">Adjuntos</label>
                  <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:newPendingFiles.length?8:0}}>
                    {newPendingFiles.map((f,i)=>(
                      <div key={i} style={{display:'flex',alignItems:'center',gap:5,padding:'4px 10px',background:'var(--accent-dim)',border:'1px solid #bfdbfe',borderRadius:20,fontSize:12}}>
                        <span>{f.type?.startsWith('image/')?'🖼️':f.type?.includes('pdf')?'📄':'📎'}</span>
                        <span style={{maxWidth:130,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'var(--accent)'}}>{f.name}</span>
                        <button type="button" onClick={()=>setNewPendingFiles(p=>p.filter((_,j)=>j!==i))} style={{background:'none',border:'none',cursor:'pointer',color:'var(--ink-2)',fontSize:14,lineHeight:1,padding:0}}>✕</button>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={()=>newFileRef.current?.click()}
                    style={{display:'flex',alignItems:'center',gap:5,background:'none',border:'1px dashed var(--border-2)',borderRadius:8,padding:'8px 14px',cursor:'pointer',fontSize:12,color:'var(--ink-2)',width:'100%',justifyContent:'center'}}>
                    📎 Hacer clic para adjuntar imágenes o documentos
                  </button>
                  <input ref={newFileRef} type="file" multiple style={{display:'none'}}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    onChange={e=>{setNewPendingFiles(p=>[...p,...Array.from(e.target.files||[])]);e.target.value='';}} />
                </div>
                <div className="form-group">
                  <label className="form-label">Equipo afectado</label>
                  <AssetSelector value={selectedAsset} onChange={setSelectedAsset} placeholder="Buscar por hostname o usuario..." />
                </div>
                {form.category !== 'seguridad' && (
                  <div className="form-group"><label className="form-label">Prioridad</label>
                    <select className="form-input" value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>
                      {Object.entries(PRIORITY_MAP).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                )}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  <div className="form-group"><label className="form-label">Reportado por *</label><input className="form-input" value={form.reported_by_name} onChange={e=>setForm(f=>({...f,reported_by_name:e.target.value}))} required /></div>
                  <div className="form-group"><label className="form-label">Email *</label><input className="form-input" type="email" value={form.reported_by_email} onChange={e=>setForm(f=>({...f,reported_by_email:e.target.value}))} required /></div>
                </div>
                <div style={{display:'flex',gap:8,justifyContent:'space-between',marginTop:8}}>
                  <button type="button" className="btn btn-secondary" onClick={() => setModalStep(2)}>Volver</button>
                  <div style={{display:'flex',gap:8}}>
                    <button type="button" className="btn btn-secondary" onClick={()=>{setShowNew(false);setSelectedAsset(null);}}>Cancelar</button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Guardando...':'Crear incidente'}</button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Panel KB — solo visible en paso 3 */}
          {modalStep === 3 && (
            <div style={{width:300,flexShrink:0,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:20,maxHeight:'90vh',overflowY:'auto',display:'flex',flexDirection:'column',gap:12}}>
              <div style={{display:'flex',alignItems:'center',gap:7,paddingBottom:12,borderBottom:'1px solid var(--bg)'}}>
                <span style={{fontSize:18}}>💡</span>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:'var(--ink)'}}>¿Ya probaste estas soluciones?</div>
                  <div style={{fontSize:11,color:'var(--ink-3)'}}>Basado en el título del ticket</div>
                </div>
              </div>
              {form.title.trim().length < 3 ? (
                <div style={{fontSize:12,color:'var(--ink-3)',textAlign:'center',padding:'20px 0'}}>
                  Escribí el título del incidente para ver sugerencias
                </div>
              ) : kbSuggestions.length === 0 ? (
                <div style={{fontSize:12,color:'var(--ink-3)',textAlign:'center',padding:'20px 0'}}>
                  No se encontraron artículos relacionados
                </div>
              ) : (
                kbSuggestions.map(a => (
                  <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer"
                    style={{display:'block',padding:'12px 14px',border:'1px solid var(--border)',borderRadius:8,textDecoration:'none',transition:'border-color 0.15s',background:'var(--bg-subtle)'}}>
                    <div style={{fontSize:13,fontWeight:600,color:'var(--accent)',marginBottom:4,display:'flex',alignItems:'center',gap:5}}>
                      {a.title}
                      <span style={{fontSize:10}}>↗</span>
                    </div>
                    {a.description && (
                      <div style={{fontSize:11,color:'var(--ink-2)',lineHeight:1.4}}>{a.description}</div>
                    )}
                    {a.category && (
                      <div style={{marginTop:6}}>
                        <span style={{fontSize:10,padding:'2px 7px',background:'#ede9fe',color:'var(--ink-2)',borderRadius:20}}>{a.category}</span>
                      </div>
                    )}
                  </a>
                ))
              )}
            </div>
          )}

          </div>
        </div>
      )}
    </div>
  );
}

